import { forgetSession, hadSession } from './session';

const SANCTUM_CSRF_PATH = '/sanctum/csrf-cookie';
const XSRF_COOKIE = 'XSRF-TOKEN';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const DEFAULT_BASE_URL = '/api';

let configuredBaseUrl: string | null = null;
let configuredLocale: string | null = null;

/**
 * Configures the client once at app startup. Each app passes its own build
 * env (`configureApi({ baseUrl: import.meta.env.VITE_BACKEND_URL })`) - the
 * package itself stays decoupled from Vite. An empty value or an
 * unsubstituted runtime placeholder (`__VITE_*__`) counts as unset so a
 * missing substitution can never produce a broken absolute URL.
 */
export function configureApi(options: { baseUrl?: string | undefined }): void {
  const raw = options.baseUrl;
  if (typeof raw !== 'string') {
    configuredBaseUrl = null;
    return;
  }
  const trimmed = raw.trim();
  configuredBaseUrl = trimmed.length === 0 || /^__VITE_\w+__$/.test(trimmed) ? null : trimmed;
}

/**
 * Sets the language every subsequent request advertises via `Accept-Language`,
 * so the backend localises the strings it renders itself (activity feed, goal
 * labels, problem documents). Call it whenever the UI language changes - and
 * invalidate the query cache afterwards, since cached responses still carry
 * the previous language. An empty value clears the header.
 */
export function setApiLocale(locale: string | null | undefined): void {
  const trimmed = typeof locale === 'string' ? locale.trim() : '';
  configuredLocale = trimmed.length === 0 ? null : trimmed;
}

/** The language currently advertised on requests; null when unset. */
export function apiLocale(): string | null {
  return configuredLocale;
}

/**
 * Backend base URL. Same-origin `/api` by default (the reverse proxy routes
 * it to the backend); {@link configureApi} overrides it.
 */
export function resolveBackendUrl(): string {
  return configuredBaseUrl ?? DEFAULT_BASE_URL;
}

function resolveCsrfUrl(): string {
  // The base URL is typically a relative path (`/api`) served same-origin via
  // the reverse proxy alongside `/sanctum/...`. When it's absolute, the CSRF
  // endpoint must travel to that origin too - otherwise the cookie lands on
  // the SPA origin and never reaches the backend.
  const base = resolveBackendUrl();
  if (/^https?:\/\//i.test(base)) {
    try {
      return new URL(SANCTUM_CSRF_PATH, base).toString();
    } catch {
      return SANCTUM_CSRF_PATH;
    }
  }
  return SANCTUM_CSRF_PATH;
}

/**
 * Error payload shape: Laravel's `{message, errors}` and RFC-7807
 * `application/problem+json` (`type`, `title`, `status`, `detail`) both map
 * onto this - the backend renders API errors as problem documents.
 */
export interface ApiErrorBody {
  message?: string;
  errors?: Record<string, string[]>;
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;
  /** Parsed `Retry-After` header (seconds), when the response carried one. */
  readonly retryAfter: number | null;

  constructor(
    status: number,
    body: ApiErrorBody | null,
    message: string,
    retryAfter: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.retryAfter = retryAfter;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401 || this.status === 419;
  }

  get validationErrors(): Record<string, string[]> {
    return this.body?.errors ?? {};
  }

  /** RFC-7807 problem type URI, when the backend sent a problem document. */
  get problemType(): string | null {
    return typeof this.body?.type === 'string' ? this.body.type : null;
  }
}

/**
 * True when `error` is an {@link ApiError} carrying a 403 or 404 - the pair of
 * statuses list/resource hooks treat as an access state rather than a generic
 * failure.
 */
export function isForbiddenError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 403 || error.status === 404);
}

/** What a 401/419 meant, for listeners that treat the two cases differently. */
export interface UnauthenticatedEvent {
  /**
   * True when this browser held a session that is now gone - an expiry worth
   * telling the user about. False for a visitor who was never logged in: the
   * bootstrap `GET /api/auth/me` probe 401s for every guest, and that is the
   * normal first paint, not an event.
   */
  expired: boolean;
}

type UnauthenticatedListener = (event: UnauthenticatedEvent) => void;

const unauthenticatedListeners = new Set<UnauthenticatedListener>();

/**
 * Subscribes to session-expiry: fires whenever ANY request through
 * {@link apiRequest} is rejected with 401/419 (cookie expired, session
 * revoked, CSRF token rotated away). The auth provider uses this to flip the
 * session to `guest`, so route guards bounce to /login instead of stranding
 * the user on a page whose every request now fails. Returns an unsubscribe
 * function.
 */
export function onUnauthenticated(listener: UnauthenticatedListener): () => void {
  unauthenticatedListeners.add(listener);
  return () => {
    unauthenticatedListeners.delete(listener);
  };
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      try {
        return decodeURIComponent(part.slice(prefix.length));
      } catch {
        return null;
      }
    }
  }
  return null;
}

function buildUrl(path: string, query?: ApiRequestOptions['query']): string {
  const base = resolveBackendUrl().replace(/\/+$/, '');
  const tail = path.startsWith('/') ? path : `/${path}`;
  const url = `${base}${tail}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs.length > 0 ? `${url}?${qs}` : url;
}

let csrfBootstrap: Promise<void> | null = null;

async function ensureCsrfCookie(): Promise<void> {
  if (readCookie(XSRF_COOKIE) !== null) return;
  if (csrfBootstrap === null) {
    const pending = fetch(resolveCsrfUrl(), {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF cookie (status ${response.status})`);
      }
    });
    // Release the handle once `pending` settles so a transient failure can
    // be retried by the next caller (but concurrent callers still share the
    // in-flight request). Attach `.finally` directly to `pending` - chaining
    // off `.catch` would leave a tiny microtask window where awaiters see
    // the rejected handle before it's cleared.
    csrfBootstrap = pending;
    void pending.finally(() => {
      if (csrfBootstrap === pending) csrfBootstrap = null;
    });
  }
  try {
    await csrfBootstrap;
  } catch {
    // Surface failure as a missing XSRF header on the caller's request.
    // The backend will reject with 419/CSRF mismatch - which the caller
    // can handle uniformly via ApiError.isUnauthenticated.
  }
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') ?? '';
  if (
    contentType.includes('application/json') ||
    contentType.includes('application/problem+json')
  ) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }
  return null;
}

function deriveMessage(status: number, body: unknown): string {
  if (body !== null && typeof body === 'object') {
    // RFC-7807 detail/title take priority; Laravel's `message` is the fallback.
    const candidates = [
      (body as { detail?: unknown }).detail,
      (body as { title?: unknown }).title,
      (body as { message?: unknown }).message,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.length > 0) return candidate;
    }
  }
  return `Request failed with status ${status}`;
}

/** Seconds from a `Retry-After` header; null for absent/HTTP-date/garbage values. */
function parseRetryAfter(response: Response): number | null {
  const header = response.headers.get('Retry-After');
  if (header === null) return null;
  const seconds = Number(header);
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  return Math.ceil(seconds);
}

export async function apiRequest<TResponse>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<TResponse> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  headers.set('X-Requested-With', 'XMLHttpRequest');
  // Only when the app has told us its language: without it the backend falls
  // back to the browser's own Accept-Language, which is the right default.
  if (configuredLocale !== null && !headers.has('Accept-Language')) {
    headers.set('Accept-Language', configuredLocale);
  }

  let body: BodyInit | null = null;
  if (options.body !== undefined && options.body !== null) {
    if (options.body instanceof FormData) {
      body = options.body;
    } else {
      headers.set('Content-Type', 'application/json');
      body = JSON.stringify(options.body);
    }
  }

  if (!SAFE_METHODS.has(method)) {
    await ensureCsrfCookie();
    const xsrf = readCookie(XSRF_COOKIE);
    if (xsrf !== null) headers.set('X-XSRF-TOKEN', xsrf);
  }

  const response = await fetch(buildUrl(path, options.query), {
    ...options,
    method,
    headers,
    body,
    credentials: 'include',
  });

  const parsed = await parseBody(response);

  if (!response.ok) {
    const errorBody =
      typeof parsed === 'object' && parsed !== null ? (parsed as ApiErrorBody) : null;
    const error = new ApiError(
      response.status,
      errorBody,
      deriveMessage(response.status, parsed),
      parseRetryAfter(response),
    );
    // DESIGN DECISION: no automatic csrf-refresh + retry on 419. A 419
    // means the session itself rotated away (expiry, logout in another
    // tab), so the right UX is the onUnauthenticated bounce-to-login below
    // - a silent retry would mask the dead session. Revisit only if
    // long-lived idle tabs start producing avoidable logouts.
    if (error.isUnauthenticated) {
      // Read the crumb before clearing it, so every listener sees the same
      // verdict no matter what order they run in (and a storm of concurrent
      // 401s reports the expiry once).
      const expired = hadSession();
      forgetSession();
      for (const listener of unauthenticatedListeners) {
        listener({ expired });
      }
    }
    throw error;
  }

  return parsed as TResponse;
}

export const api = {
  get: <T>(path: string, options?: ApiRequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: ApiRequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};
